import React from 'react';
import { Users, Plus, X as XIcon, UserCircle2, AlertTriangle, CheckCircle2, Building2, Shield } from 'lucide-react';
import { cleanRoles as cleanScenarioRoles } from './ScenarioRoleField';
import { persistRoleChanges, openAssignPersonnel } from './AssignPersonnelModal';

// Per-card staffing panel: shows each role's coverage + assigned chips. The
// actual picker is the page-level <AssignPersonnelModal /> — this panel only
// summons it via an event. That keeps the modal OUTSIDE the card tree, so the
// background refetches that remount timeline cards can never blink/reset it
// (the old inline dropdown/modal was visibly unstable because of exactly that).
//
// roleAssignments is stored on meta as { [roleName]: [{ type:'user', id, name, via? }] }.
// Department/crew picks are expanded into their member users so every consumer
// (staffing gate, participants widget, roster memory, task visibility) keeps
// working on user ids; `via` remembers the group they came from for the UI.
const coverageFor = (assignedList = []) => (assignedList || []).length;

export const cardIsFullyAssigned = (item) => {
  const roles = cleanScenarioRoles(item?.meta?.scenarioRoles);
  if (!roles.length) return true;
  const ra = item?.meta?.roleAssignments || {};
  return roles.every((r) => coverageFor(ra[r.role]) >= r.count);
};

export const cardHasRoles = (item) => cleanScenarioRoles(item?.meta?.scenarioRoles).length > 0;

const RoleAssignmentPanel = ({ item, incident, onRefresh, allItems = [], variant = 'light' }) => {
  const dk = variant === 'dark';
  const roles = cleanScenarioRoles(item?.meta?.scenarioRoles);

  const [assignments, setAssignments] = React.useState(item?.meta?.roleAssignments || {});
  const [saving, setSaving] = React.useState(false);
  // While a removal is being saved, incoming (possibly stale) item props must
  // not clobber the optimistic state.
  const pendingRef = React.useRef(0);
  const assignmentsRef = React.useRef(assignments);
  React.useEffect(() => { assignmentsRef.current = assignments; }, [assignments]);
  React.useEffect(() => {
    if (pendingRef.current > 0) return;
    const next = item?.meta?.roleAssignments || {};
    assignmentsRef.current = next;
    setAssignments(next);
  }, [item?.id, JSON.stringify(item?.meta?.roleAssignments || {})]);

  const removeEntries = (roleName, predicate) => {
    const cur = assignmentsRef.current;
    const next = (cur[roleName] || []).filter((a) => !predicate(a));
    const optimistic = { ...cur, [roleName]: next };
    assignmentsRef.current = optimistic;
    setAssignments(optimistic);
    pendingRef.current += 1;
    setSaving(true);
    persistRoleChanges(incident, item, allItems, { [roleName]: next }, {
      onSettled: () => {
        pendingRef.current -= 1;
        if (pendingRef.current === 0) { setSaving(false); onRefresh?.(); }
      },
    });
  };

  const allAssigned = roles.every((r) => coverageFor(assignments[r.role]) >= r.count);

  // Render entries added via a department/crew as ONE group chip (with member count).
  const chipify = (list = []) => {
    const groups = new Map();
    const singles = [];
    list.forEach((a) => {
      if (a?.via?.id) {
        const k = `${a.via.type}:${a.via.id}`;
        if (!groups.has(k)) groups.set(k, { via: a.via, members: [] });
        groups.get(k).members.push(a);
      } else if (a) singles.push(a);
    });
    return { groups: [...groups.values()], singles };
  };

  if (!roles.length) return null;

  return (
    <div
      data-testid={`role-assignment-panel-${item.id}`}
      className={`mt-2 rounded-md border px-3 py-2.5 ${dk ? 'border-slate-700 bg-slate-800/70' : 'border-violet-200 bg-violet-50/50'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${dk ? 'text-cyan-400' : 'text-violet-700'}`}>
          <Users className="h-3.5 w-3.5" /> Assign Personnel
          {allAssigned ? (
            <span className={`inline-flex items-center gap-0.5 ml-1 normal-case tracking-normal font-semibold ${dk ? 'text-emerald-400' : 'text-emerald-600'}`} data-testid={`assign-complete-${item.id}`}>
              <CheckCircle2 className="h-3 w-3" /> Fully staffed
            </span>
          ) : (
            <span className={`inline-flex items-center gap-0.5 ml-1 normal-case tracking-normal font-semibold ${dk ? 'text-amber-400' : 'text-amber-600'}`} data-testid={`assign-unassigned-${item.id}`}>
              <AlertTriangle className="h-3 w-3" /> Needs personnel
            </span>
          )}
        </span>
        {saving && <span className={`text-[10px] font-semibold ${dk ? 'text-slate-400' : 'text-gray-400'}`} data-testid={`assign-saving-${item.id}`}>Saving…</span>}
      </div>

      <div className="space-y-2">
        {roles.map((r) => {
          const list = assignments[r.role] || [];
          const have = coverageFor(list);
          const filled = have >= r.count;
          const { groups, singles } = chipify(list);
          return (
            <div key={r.role} className={`rounded border px-2.5 py-2 ${dk ? 'border-slate-700 bg-slate-900/70' : 'border-violet-100 bg-white'}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${dk ? 'text-slate-200' : 'text-gray-800'}`}>
                  {r.count > 1 ? `${r.count}× ` : ''}{r.role}
                </span>
                <span className={`text-[11px] font-bold ${filled ? (dk ? 'text-emerald-400' : 'text-emerald-600') : (dk ? 'text-amber-400' : 'text-amber-600')}`} data-testid={`role-coverage-${item.id}-${r.role}`}>
                  {have}/{r.count}
                </span>
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {groups.map((g) => (
                  <span
                    key={`${g.via.type}-${g.via.id}`}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${dk ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-gray-100 border-gray-200 text-gray-700'}`}
                    title={g.members.map((m) => m.name).join(', ')}
                    data-testid={`assigned-group-chip-${item.id}-${g.via.id}`}
                  >
                    {g.via.type === 'department'
                      ? <Building2 className={`h-3 w-3 ${dk ? 'text-cyan-400' : 'text-indigo-500'}`} />
                      : <Shield className={`h-3 w-3 ${dk ? 'text-cyan-400' : 'text-teal-600'}`} />}
                    {g.via.name} · {g.members.length}
                    <button type="button" onClick={() => removeEntries(r.role, (a) => a?.via?.id === g.via.id && a?.via?.type === g.via.type)} className={`ml-0.5 ${dk ? 'text-slate-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`} data-testid={`assigned-group-remove-${item.id}-${g.via.id}`}>
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {singles.map((a) => (
                  <span
                    key={a.id}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${dk ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-gray-100 border-gray-200 text-gray-700'}`}
                    data-testid={`assigned-chip-${item.id}-${a.id}`}
                  >
                    <UserCircle2 className={`h-3 w-3 ${dk ? 'text-cyan-400' : 'text-blue-500'}`} />
                    {a.name}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openAssignPersonnel({
                          itemId: item.id,
                          role: r.role,
                          variant,
                          item,
                          replaceUserId: a.id,
                          replaceUserName: a.name,
                        });
                      }}
                      className={`ml-1 rounded px-1 py-0.5 text-[9px] font-bold ${dk ? 'text-cyan-400 hover:bg-cyan-950/60' : 'text-violet-600 hover:bg-violet-50'}`}
                      aria-label={`Change ${a.name || 'assignee'}`}
                      data-testid={`assigned-change-${item.id}-${a.id}`}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeEntries(r.role, (x) => x.id === a.id);
                      }}
                      className={`ml-0.5 ${dk ? 'text-slate-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                      aria-label={`Remove ${a.name || 'assignee'}`}
                      data-testid={`assigned-remove-${item.id}-${a.id}`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}

                {!filled && (
                  <button
                    type="button"
                    data-testid={`assign-add-${item.id}-${r.role}`}
                    onClick={() => openAssignPersonnel({ itemId: item.id, role: r.role, variant, item })}
                    className={`inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-[11px] font-semibold ${dk ? 'border-cyan-700 text-cyan-400 hover:bg-cyan-950/50' : 'border-violet-300 text-violet-600 hover:bg-violet-50'}`}
                  >
                    <Plus className="h-3 w-3" /> Assign
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoleAssignmentPanel;
